import { type MeResponse, NOC_STAGES, isAdminRole } from "@seagames/contracts";
import type { LinkProps } from "@tanstack/react-router";

/**
 * Every sector has its own Dashboard (user, 2026-10-31), the first row of
 * its sidebar group, showing only that sector's data. Sector 4's is the
 * Contingent Dashboard (it already was the delegation in one page).
 */
export type SectorNumber = (typeof NOC_STAGES)[number]["stage"];

export const SECTOR_DASHBOARDS = {
	1: "/dashboard/entries",
	2: "/dashboard/accreditation",
	3: "/dashboard/logistics",
	4: "/contingent",
} as const satisfies Record<SectorNumber, NonNullable<LinkProps["to"]>>;

/**
 * The overall Dashboard (Overview, above Central Report Engine; user
 * 2026-10-31): the four sectors at a glance — the admins', and a NOC's over
 * its own reads (user, 2026-10-31). A NOC still LANDS on its current
 * sector's dashboard (`homeDashboardFor`).
 */
export const OVERALL_DASHBOARD = "/dashboard" as const satisfies NonNullable<LinkProps["to"]>;

type Session = Pick<MeResponse, "active_role" | "noc_stage">;

/** The sectors' sidebar (and so the sector dashboards) is the admins' and a NOC's (nav.ts `SECTOR_ROLES`). */
export const hasSectorDashboards = (s: Session): boolean => isAdminRole(s.active_role) || s.active_role === "ORGANIZATION";

/**
 * Where "/" sends a login with sector dashboards: a NOC to the dashboard of
 * the sector it has reached (its newest open stage — what it is working on
 * now); the admins to the overall Dashboard (every sector at a glance).
 * Null for every other role ("/" stays their dashboard).
 */
export function homeDashboardFor(s: Session): (typeof SECTOR_DASHBOARDS)[SectorNumber] | typeof OVERALL_DASHBOARD | null {
	if (!hasSectorDashboards(s)) return null;
	if (s.active_role === "ORGANIZATION") {
		const stage = s.noc_stage?.stage ?? 1;
		const sector = (NOC_STAGES.find((x) => x.stage === stage)?.stage ?? 1) as SectorNumber;
		return SECTOR_DASHBOARDS[sector];
	}
	return OVERALL_DASHBOARD;
}

/** Whether `sector` is open for this session: always, but for a NOC whose stage has not reached it. */
export const sectorOpenFor = (s: Session, sector: SectorNumber): boolean =>
	s.active_role !== "ORGANIZATION" || !s.noc_stage || s.noc_stage.stage >= sector;
